import { type ReactNode } from 'react';
import { Button, type ButtonVariant } from './Button';
import { cn } from './cn';

/**
 * EmptyState — Design System v2 (docs/design-system-v2.html §5.empty).
 *
 * Pictograma + título + subtítulo + ação. Usado quando uma lista está
 * vazia OU quando o usuário ainda não configurou algo.
 */

interface EmptyStateProps {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: {
    label: ReactNode;
    onClick: () => void;
    /** Variante do botão de ação. Default `primary`. */
    variant?: ButtonVariant;
  };
  className?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center px-6 py-12 gap-3',
        className,
      )}
    >
      <div className="w-16 h-16 rounded-xl bg-bg-muted grid place-items-center text-text-faint-v2">
        {icon}
      </div>
      <h3 className="font-display text-[15px] font-bold text-text-primary">{title}</h3>
      {description !== undefined && (
        <p className="text-text-tertiary max-w-[340px]">{description}</p>
      )}
      {action !== undefined && (
        <Button
          size="md"
          variant={action.variant ?? 'primary'}
          onClick={action.onClick}
          className="mt-1"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
